import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Collapse,
  Box,
  Typography,
} from "@material-ui/core";
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Business as BusinessIcon,
} from "@material-ui/icons";
import { Tooltip } from "@material-ui/core";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import AssignCompaniesModal from "../../components/AssignCompaniesModal";
import { fmtDate, fmtDateTimeShort } from "../../translate/localeHelper";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  summaryRow: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  summaryCard: {
    padding: theme.spacing(2),
    minWidth: 200,
    display: "flex",
    flexDirection: "column",
  },
  summaryLabel: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
  },
  summaryValue: { fontSize: 26, fontWeight: 600 },
  revenue: { color: "#2e7d32" },
  email: { fontSize: 12, color: theme.palette.text.secondary },
}));

const fmtDateSafe = (d) => {
  try {
    return d ? fmtDate(d) : "—";
  } catch {
    return "—";
  }
};
const fmtDateTimeSafe = (d) => {
  try {
    return d ? fmtDateTimeShort(d) : "—";
  } catch {
    return "—";
  }
};

const SuperAdminResellers = () => {
  const classes = useStyles();
  const [resellers, setResellers] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [assigning, setAssigning] = useState(null); // reseller para el modal de asignación

  const fetchData = async () => {
    try {
      const { data } = await api.get("/superadmin/resellers");
      setResellers(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const totalCompanies = resellers.reduce((s, r) => s + (r.companiesCount || 0), 0);
  const totalRevenue = resellers.reduce((s, r) => s + (r.monthlyRevenue || 0), 0);

  return (
    <div className={classes.root}>
      <Typography variant="h5" gutterBottom>
        Resellers y control económico
      </Typography>

      <div className={classes.summaryRow}>
        <Paper className={classes.summaryCard} variant="outlined">
          <span className={classes.summaryLabel}>Resellers activos</span>
          <span className={classes.summaryValue}>{resellers.length}</span>
        </Paper>
        <Paper className={classes.summaryCard} variant="outlined">
          <span className={classes.summaryLabel}>Empresas creadas por resellers</span>
          <span className={classes.summaryValue}>{totalCompanies}</span>
        </Paper>
        <Paper className={classes.summaryCard} variant="outlined">
          <span className={classes.summaryLabel}>Revenue mensual estimado (USD)</span>
          <span className={`${classes.summaryValue} ${classes.revenue}`}>
            ${totalRevenue}
          </span>
        </Paper>
      </div>

      <Paper variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Reseller</TableCell>
              <TableCell>Marca</TableCell>
              <TableCell align="center">Empresas</TableCell>
              <TableCell align="right">Revenue mensual</TableCell>
              <TableCell>Última actividad</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {resellers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="textSecondary" align="center">
                    No hay resellers todavía. Creá uno desde "Superadmins"
                    asignándole una marca o permisos limitados.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              resellers.map((r) => (
                <React.Fragment key={r.id}>
                  <TableRow>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() =>
                          setExpandedId(expandedId === r.id ? null : r.id)
                        }
                      >
                        {expandedId === r.id ? (
                          <KeyboardArrowUp />
                        ) : (
                          <KeyboardArrowDown />
                        )}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <div>
                        <strong>{r.name}</strong>
                      </div>
                      <div className={classes.email}>{r.email}</div>
                    </TableCell>
                    <TableCell>
                      <Chip label={r.whitelabelScope?.name || "—"} size="small" />
                    </TableCell>
                    <TableCell align="center">{r.companiesCount}</TableCell>
                    <TableCell align="right" style={{ color: "#2e7d32", fontWeight: 600 }}>
                      ${r.monthlyRevenue}
                    </TableCell>
                    <TableCell>{fmtDateTimeSafe(r.lastActiveAt)}</TableCell>
                    <TableCell align="center">
                      {r.whitelabelScopeId ? (
                        <Tooltip title="Asignar empresas a este reseller">
                          <IconButton size="small" onClick={() => setAssigning(r)}>
                            <BusinessIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <span style={{ color: "gray", fontSize: 11 }}>WAIA base</span>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={7} style={{ padding: 0, border: 0 }}>
                      <Collapse in={expandedId === r.id} unmountOnExit>
                        <Box p={2}>
                          <Typography variant="subtitle2" gutterBottom>
                            Empresas de {r.name}
                          </Typography>
                          {(!r.companies || r.companies.length === 0) ? (
                            <Typography variant="body2" color="textSecondary">
                              Sin empresas creadas todavía
                            </Typography>
                          ) : (
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>ID</TableCell>
                                  <TableCell>Nombre</TableCell>
                                  <TableCell>Plan</TableCell>
                                  <TableCell>Estado</TableCell>
                                  <TableCell>Creada</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {r.companies.map((c) => (
                                  <TableRow key={c.id}>
                                    <TableCell>{c.id}</TableCell>
                                    <TableCell>{c.name}</TableCell>
                                    <TableCell>
                                      <Chip label={c.plan} size="small" />
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        label={c.operationStatus || (c.active ? "active" : "inactive")}
                                        size="small"
                                        variant="outlined"
                                      />
                                    </TableCell>
                                    <TableCell>{fmtDateSafe(c.createdAt)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      <AssignCompaniesModal
        reseller={assigning}
        open={!!assigning}
        onClose={() => setAssigning(null)}
        onSaved={fetchData}
      />
    </div>
  );
};

export default SuperAdminResellers;
